/**
 * Flow:
 * 1. Call the API to get the similar projects (using the project_id)
 * 2. Display the similar projects in a table (API returns the 10 most similar projects)
 * 3. Display the project we are comparing to in a card
 * 
 * TODO:
 *  - Maybe add pagination to only up to 50 total projects?
 */

import { useEffect } from "react";
import { useState } from "react";
import { SimilarProjectInterface } from "../Interface/SimilarProjectInterface";
import { Badge } from "@/components/ui/badge";
import { ProjectProps } from "@/interfaces/project_types";
import { getSimilarProjects } from "../api/similar_projects";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
  } from "@/components/ui/tooltip"
import { InfoIcon } from "lucide-react";
import { TitleHeaderHyperlink } from "@/components/Dashboard/UI/Cards/Utils/TitleHeaderHyperlink";

export function SimilarProjects({ project }: { project: ProjectProps }) {
    const [_similarProjects, setSimilarProjects] = useState<SimilarProjectInterface[]>([])
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        setIsLoading(true)
        if (project.project_id) {
            getSimilarProjects(project.project_id).then((projects) => {
                setSimilarProjects(projects)
                setIsLoading(false)
            })
        }
        
    }, [project.project_id])
    
    return (
        <div className="flex flex-col gap-5 bg-stone-50 dark:bg-stone-900">
            <Header />

            {isLoading ? <Loading /> : _similarProjects.length === 0 ? <NoSimilarProjects /> : <SimilarProjectTable similar_projects={_similarProjects} project_to_compare={project} />}
        </div>
    )
}

function Loading() {
    return (
        <div className="flex flex-col gap-5">
            <h3 className="text-base font-semibold">(Loading...)</h3>
        </div>
    )
}
function Header() {
    return (
        <div className="flex flex-row gap-3">
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="flex flex-row gap-2 dark:text-white">
                            <h3>Projects for Reference:</h3>
                            <InfoIcon className="w-4 h-4" />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>
                            Projects that are similar to the current project you're on.
                            <br />
                            <br />
                            The similarity is based on the project name, client name, city, size, and project type.
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    )
}

function NoSimilarProjects() {
    return (
        <div className="flex flex-col gap-5">
            <h3 className="text-base font-semibold">(No similar projects found)</h3>
            <p className="text-gray-500">
                (This project is either unique or missing attributes that are used to determine similarity.)
            </p>
        </div>
    )
}

// This is the component that displays the similar projects in a table
function SimilarProjectTable({ similar_projects }: { similar_projects: SimilarProjectInterface[], project_to_compare: ProjectProps }) {
    return (
        <div className="grid grid-cols-5 gap-5 pb-6 ">
            {similar_projects.map((project) => (
            <SimilarProjectComponent key={project.project_id} project={project} />
            ))}
        </div>
    )
}

// This is the component that displays a single similar project
function SimilarProjectComponent({ project }: { project: SimilarProjectInterface }) {
    return (
        <div className="flex flex-row gap-4 p-5 border-t-2 border-orange-700 rounded-lg shadow-lg min-w-60 bg-gray-50 dark:bg-stone-900">
            <div className="flex flex-col w-full gap-2">
                <TitleHeaderHyperlink title={project.project_name} folder_location={project.folder_location ?? ""} />
                
                <SimilarProjectBadgeContainer project={project} />

                <p className="text-sm text-gray-600 dark:text-gray-400">Client: {project.client_name || <span className="italic text-gray-500">(No client name)</span>}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">City: {project.city || <span className="italic text-gray-500">(No city)</span>}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Type: {project.template || <span className="italic text-gray-500">(No template)</span>}</p>
            </div>
        </div>
    )
}

/**
 * This component displays the badges for the similar projects
 * 
 * This is to give easy visual feedback to the user about how similar the projects are.
 * 
 * @param project - The project to compare to
 * @param project_to_compare - The project to compare
 * @returns 
 */
function SimilarProjectBadgeContainer({ project }: { project: SimilarProjectInterface }) {
    return (
        <div className="grid grid-cols-3 ">
            {project.similarity_tags.map((tag) => (
                <BadgeFactory key={tag} tag={tag} />
            ))}
        </div>
    )
}

/**
 * This function returns a badge based on the tag
 * 
 * @param tag - The tag to return a badge for
 * @returns A badge component or null if the tag is unknown
 */
function BadgeFactory({ tag }: { tag: string }) {
    switch (tag) {
        case "project_name":
            return <Badge className="bg-sky-600 hover:bg-sky-700 dark:bg-sky-500 dark:hover:bg-sky-600">Name</Badge>
        case "client":
            return <Badge className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600">Client</Badge>
        case "template":
            return <Badge className="bg-rose-600 hover:bg-rose-700 dark:bg-rose-500 dark:hover:bg-rose-600">Type</Badge>
        case "city":
            return <Badge className="bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600">City</Badge>
        case "project_size":
            return <Badge className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600">Size</Badge>
        default:
            console.error(`Unknown tag: ${tag}`)
            return null
    }
}
